import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import type { AuthProvider, AuthStatus, Session } from './types';

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  provider: AuthProvider;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderComponentProps {
  provider: AuthProvider;
}

export function AuthProviderComponent({
  provider,
  children,
}: PropsWithChildren<AuthProviderComponentProps>) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('unknown');

  useEffect(() => {
    let cancelled = false;
    let sessionRevision = 0;
    const unsubscribe = provider.onSessionChange((s) => {
      sessionRevision += 1;
      setSession(s);
      setStatus(s ? 'authenticated' : 'guest');
    });
    provider
      .getSession()
      .then((s) => {
        if (cancelled || sessionRevision > 0) return;
        setSession(s);
        setStatus(s ? 'authenticated' : 'guest');
      })
      .catch(() => {
        if (!cancelled && sessionRevision === 0) setStatus('guest');
      });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [provider]);

  const value = useMemo(() => ({ status, session, provider }), [status, session, provider]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProviderComponent');
  return ctx;
}
